import {
    Box,
    Card,
    CardContent,
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Typography
} from "@mui/material";
import {Chart} from "react-google-charts";
import {DashboardService} from "../../services/dashboard-service";
import {useEffect} from "react";
import * as React from "react";
import {translate} from "../../translate/translate";
import Select from "@mui/material/Select";
import {TotalChart} from "../../models/basic/dashboard";
import {formatNumber} from "../../utils/format-number";

const TotalChartsComponent = () => {

    const service = new DashboardService();
    const [totalData, setTotalData] = React.useState<any>();
    const [dataSourceYears, setDataSourceYears] = React.useState<number[]>([]);
    const [selectedYears, setSelectedYears] = React.useState<number[]>([]);

    useEffect(() => {
        loadYears();
        loadTotalChart();
    }, []);

    useEffect(() => {
        loadTotalChart();
    }, [selectedYears]);

    const initializeLastFiveYears = (years: number[]) => {
        const y = years.slice(0, 5);
        setSelectedYears(y);
    }

    const loadYears = () => {
        service.getYearsSummary().then(response => {
            setDataSourceYears(response.data.years);
            initializeLastFiveYears(response.data.years);
        }).catch(() => {
            setDataSourceYears([]);
        });
    }

    const loadTotalChart = () => {
        const formattedYears = selectedYears.join(",");
        service.getTotalCharts(formattedYears).then(response => {
            setTotalData(transformTotalData(response.data));
        });
    }

    const renderYears = (selected: any) => {
        if (allYearsSelected()) {
            return translate.t('all');
        }
        const sorted = selected.sort((a: number, b: number) => a - b);
        return sorted.join(", ");
    };

    const allYearsSelected = () => {
        return selectedYears.length === dataSourceYears.length;
    }

    const transformTotalData = (data: TotalChart) => {
        const chartData: any[] = [
            ["Ano",
                { type: "number", label: "Total NRE" }, { role: "tooltip", type: "string", p: { html: true } },
                { type: "number", label: "Total Amortized" }, { role: "tooltip", type: "string", p: { html: true } },
                { type: "number", label: "Open NRE" }, { role: "tooltip", type: "string", p: { html: true } }
            ]
        ];

        for (let i = 0; i < data.years.length; i++) {
            const year = data.years[i];
            const totalNre = Number(data.total_nre[i]);
            const totalAmortized = Number(data.total_amortized[i]);
            const openNre = Number(data.open_nre[i]);

            const row = [
                year.toString(),
                totalNre,
                tooltip("Total NRE", year, totalNre),
                totalAmortized,
                tooltip("Total Amortized", year, totalAmortized),
                openNre,
                tooltip("Open NRE", year, openNre),
            ];

            chartData.push(row);
        }

        return chartData;
    }

    const tooltip = (legenda: string, ano: number, valor: number) => {
        return `
            <div style="text-align: center; padding: 0.5rem;">
                <strong>${legenda}</strong><br/>
                <div style="white-space: nowrap">${ano}: ${formatNumber(valor, true)}</div>
            </div>
        `;
    }

    const options = {
        legend: {
            position: "bottom",
            textStyle: {
                color: "#333",
                fontSize: 13,
            },
            format: "R$ #,##0",
        },
        chartArea: {
            left: 130,
            width: '95%',
            top: 30,
        },
        bar: {
            groupWidth: "30%",
        },
        colors: ["#8979FF", "#FF928A", "#3CC3DF"],
        annotations: {
            alwaysOutside: true,
            textStyle: {
                fontSize: 13,
                color: '#000',
                auraColor: 'none',
            },
        },
        isStacked: false,
        vAxis: {
            format: "R$ #,##0",
            minorGridlines: {
                color: 'transparent',
            }
        },
        tooltip: { isHtml: true },
    };

    return (
        <Card>
            <CardContent>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <Typography variant={"h5"}>
                        Total NRE, Total Amortized e Open NRE
                    </Typography>

                    <FormControl sx={{width: "300px"}}>
                        <InputLabel>
                            {translate.t("year")}
                        </InputLabel>
                        <Select
                            name="year"
                            label={translate.t("year")}
                            variant={"outlined"}
                            fullWidth
                            autoFocus={false}
                            value={selectedYears}
                            onChange={(event: any) => {
                                if (event.target.value.includes(0)) {
                                    if (!allYearsSelected()) {
                                        const years = dataSourceYears?.map((year: any) => year);
                                        console.log(years);
                                        setSelectedYears(years || []);
                                        return;
                                    }
                                    setSelectedYears([]);
                                    return;
                                }
                                const validValues = event.target.value
                                    .map((value: any) => Number(value))
                                    .filter((value: number) => Number.isFinite(value));
                                setSelectedYears(validValues);
                            }}
                            multiple
                            renderValue={(selected) => renderYears(selected)}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: '400px',
                                    },
                                },
                                disableAutoFocusItem: true,
                            }}
                        >
                            <MenuItem key={0} value={0}>
                                <Checkbox checked={allYearsSelected()}/>
                                <ListItemText primary={
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography variant="body1">{translate.t('all')}</Typography>
                                        {allYearsSelected() && <Typography variant="body2"
                                                                           color="gray">{translate.t('clear')}</Typography>}
                                    </Box>
                                }/>
                            </MenuItem>
                            {dataSourceYears?.map((year: any) => (
                                <MenuItem key={year} value={year}>
                                    <Checkbox checked={selectedYears.includes(year)}/>
                                    <ListItemText primary={year}/>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{
                    overflowX: "auto",
                    overflowY: "hidden",
                    maxWidth: "98%",
                }}>
                    <Chart
                        chartType="ColumnChart"
                        width="100%"
                        height="350px"
                        data={totalData}
                        options={options}
                        chartLanguage="pt-BR"
                    />
                </Box>
            </CardContent>
        </Card>
    )
}

export default TotalChartsComponent;
